import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { EyeMark } from "../components/Logo";

const Section = ({ id, title, children, testid }) => (
  <section id={id} data-testid={testid} className="scroll-mt-24 rounded-lg border border-[#D4AF37]/15 bg-[#06333E] p-6 sm:p-10">
    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-wide text-[#F8FAFC]">{title}</h2>
    <div className="mt-4 space-y-4 text-sm sm:text-base text-[#B8C7CC] leading-relaxed">{children}</div>
  </section>
);

const Policies = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-[#052730] pt-10 pb-20" data-testid="policies-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#FCD34D] transition-colors" data-testid="policies-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to the Archive
          </Link>
          <EyeMark className="w-8 h-8" />
        </div>

        <p className="mt-12 font-mono-alt text-xs uppercase tracking-[0.35em] text-[#D4AF37]">The fine print</p>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-[#F8FAFC]">Policies & Terms</h1>
        <p className="mt-3 text-sm text-[#94A3B8]">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

        <nav className="mt-8 flex flex-wrap gap-3" data-testid="policies-nav">
          {[
            ["#privacy", "Privacy Policy"],
            ["#terms", "Terms of Service"],
            ["#refunds", "Refunds & Delivery"],
            ["#contact", "Contact"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              data-testid={`policies-nav-${href.slice(1)}`}
              className="rounded-full border border-[#D4AF37]/25 px-4 py-2 text-xs font-mono-alt uppercase tracking-[0.2em] text-[#FCD34D] hover:bg-[#D4AF37]/10 transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="mt-10 space-y-8">
          <Section id="privacy" title="Privacy Policy" testid="policy-privacy">
            <p className="text-[#FCD34D] font-medium">ggelves.com is operated by Vidhata Enterprises.</p>
            <p>Vidhata Enterprises, operating as The Gg Elves Archive ("we", "us"), runs ggelves.com and the store for <em>The End of the Obvious</em>. This page explains what we collect and why.</p>
            <p><strong className="text-[#F8FAFC]">What we collect.</strong> When you buy, we store your email address and order details (product, amount, time). Payment card details are processed directly by our payment provider (Razorpay) and never touch our servers. We also set a small browser storage entry to remember your offer countdown.</p>
            <p><strong className="text-[#F8FAFC]">How we use it.</strong> Your email is used only to deliver your purchase, resend your download link on request, and resolve order issues. We do not sell, rent, or share your personal data with third parties for marketing.</p>
            <p><strong className="text-[#F8FAFC]">Emails.</strong> We send transactional emails only (order confirmation and download link). No newsletters or promotional email are sent without separate consent.</p>
            <p><strong className="text-[#F8FAFC]">Data retention & rights.</strong> Order records are kept for accounting and support purposes. You may request a copy or deletion of your personal data anytime by emailing us — see Contact below.</p>
            <p><strong className="text-[#F8FAFC]">Security.</strong> All traffic is encrypted over HTTPS. Payment processing is PCI-DSS compliant via our payment provider.</p>
          </Section>

          <Section id="terms" title="Terms of Service" testid="policy-terms">
            <p className="text-[#FCD34D] font-medium">ggelves.com is operated by Vidhata Enterprises.</p>
            <p><strong className="text-[#F8FAFC]">The product.</strong> "The End of the Obvious — Reader's Edition" is a digital ebook (PDF, 154 pages). Upon completed payment you receive a personal, non-transferable license to download and read it on your own devices.</p>
            <p><strong className="text-[#F8FAFC]">License.</strong> Your purchase grants a single-reader license. You may not resell, redistribute, upload, or publicly share the PDF or your download link. All content remains the intellectual property of the Gg Elves Archive.</p>
            <p><strong className="text-[#F8FAFC]">Acceptable use.</strong> You agree not to misuse the site, attempt to bypass payment or access controls, or scrape content at abusive volumes.</p>
            <p><strong className="text-[#F8FAFC]">Pricing.</strong> Promotional pricing (e.g. the early-reader offer) is time-limited and may change without notice. The price shown at checkout is the price you pay.</p>
            <p><strong className="text-[#F8FAFC]">Availability.</strong> We aim for uninterrupted instant delivery but do not guarantee 100% uptime. If a technical failure prevents delivery of a paid order, we will deliver manually or refund in full.</p>
          </Section>

          <Section id="refunds" title="Refund & Delivery Policy" testid="policy-refunds">
            <p><strong className="text-[#F8FAFC]">Instant delivery.</strong> Your download is available immediately after payment on the confirmation page, and a personal download link is emailed to the address you provided. If the email doesn't arrive, check spam, then use "Lost your download link?" in the site footer.</p>
            <p><strong className="text-[#F8FAFC]">Refunds.</strong> Digital goods are generally non-refundable once downloaded, as they cannot be returned. However, we will issue a full refund within 14 days of purchase if:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You were charged but never received access, or</li>
              <li>The file is corrupted or unreadable and we cannot fix it, or</li>
              <li>You were charged twice for the same order.</li>
            </ul>
            <p>To request a refund, email us with your order email and order ID — see Contact below. Approved refunds are processed to the original payment method within 5–7 business days.</p>
          </Section>

          <Section id="contact" title="Contact" testid="policy-contact">
            <p>For order support, refunds, data requests, or anything else about your purchase:</p>
            <div className="space-y-1.5">
              <p><strong className="text-[#F8FAFC]">Business name:</strong> Vidhata Enterprises (operating as Gg Elves Archive)</p>
              <p>
                <strong className="text-[#F8FAFC]">Email:</strong>{" "}
                <a href="mailto:support@ggelves.com" className="text-[#FCD34D] underline underline-offset-4 hover:text-[#D4AF37]" data-testid="policy-contact-email">
                  support@ggelves.com
                </a>
              </p>
              <p>
                <strong className="text-[#F8FAFC]">Phone:</strong>{" "}
                <a href="tel:+917015043870" className="text-[#FCD34D] underline underline-offset-4 hover:text-[#D4AF37]" data-testid="policy-contact-phone">
                  +91 7015043870
                </a>
              </p>
              <p><strong className="text-[#F8FAFC]">Address:</strong> 24, Ghittorni, New Delhi – 110047, Delhi, India</p>
            </div>
            <p>Please include the email you bought with and, if you have it, your order ID. We aim to reply within 2 business days.</p>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Policies;
